import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Button,
  Avatar,
  Divider,
  useToast,
  Flex,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Select,
  Switch,
  IconButton,
  Tabs, 
  TabList, 
  Tab, 
  TabPanels, 
  TabPanel,
  InputGroup,
  InputRightElement,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiEdit2, FiSave, FiUpload, FiUser, FiLock, FiSettings, FiCheck, FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';
import { getUserSettings, updateUserSettings } from '../../../utils/userSettings';

const Profile = () => {
  // Get user data from session storage or user settings
  const userInitialData = getUserSettings()?.user || JSON.parse(sessionStorage.getItem('user')) || {};
  console.log('User Initial Data:', userInitialData);
  const tenantId = getUserSettings()?.tenantId || userInitialData?.tenant_ids?.[0];
  console.log('Tenant ID:', tenantId);
  
  const [user, setUser] = useState(userInitialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    contentChanges: true,
    securityAlerts: true
  });
  
  const avatarInputRef = useRef();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch user details on component mount
  useEffect(() => {
    if (tenantId && user.user_id) {
      fetchUserDetails();
    } else {
      setIsLoading(false);
      setError('User information not found. Please log in again.');
    }
  }, [tenantId, user.user_id]);

  const fetchUserDetails = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient(`/users/${user.user_id}`);
      
      if (response.data) {
        setUser(response.data);
        
        // Set notification preferences from user data if they exist
        if (response.data.preferences) {
          setNotifications({
            emailUpdates: response.data.preferences.emailUpdates ?? true,
            contentChanges: response.data.preferences.contentChanges ?? true,
            securityAlerts: response.data.preferences.securityAlerts ?? true
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user profile. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // If canceling edit, reset form to original data
      setUser(userInitialData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationChange = (setting) => {
    setNotifications(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleAvatarClick = () => {
    avatarInputRef.current.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size and type
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        title: "File too large",
        description: "Profile image must be less than 5MB",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a JPG, PNG, or GIF image",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('avatar', file);
      
      // Upload avatar to server
      const response = await apiClient.post(`/users/${user.user_id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.avatar_url) {
        setUser(prev => ({
          ...prev,
          avatar_url: response.data.avatar_url
        }));
        
        toast({
          title: "Avatar updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload profile image",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validatePasswordChange = () => {
    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return false;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return false;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return false;
    }
    
    setPasswordError('');
    return true;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordChange()) return;
    
    try {
      setIsSaving(true);
      
      await apiClient.post(`/users/${user.user_id}/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast({
        title: "Password changed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const saveProfile = async () => {
    try {
      setIsSaving(true);
      
      // Create an object with only the fields we want to update
      const updateData = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        job_title: user.job_title,
        preferences: {
          ...notifications
        }
      };
      
      const response = await apiClient.put(`/users/${user.user_id}`, updateData);
      
      if (response.data) {
        // Update session storage and/or user settings
        const updatedUser = response.data;
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        updateUserSettings({ user: updatedUser });
        
        setUser(updatedUser);
        setIsEditing(false);
        
        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Update failed",
        description: error.response?.data?.message || "Failed to update profile",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'admin': 'red',
      'editor': 'green',
      'author': 'blue',
      'contributor': 'purple',
      'viewer': 'gray'
    };
    return colors[roleName?.toLowerCase()] || 'gray';
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="300px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      <Card mb={8}>
        <CardHeader bg="blue.50">
          <HStack justify="space-between">
            <Heading size="lg">My Profile</Heading>
            <Button
              leftIcon={isEditing ? <FiX /> : <FiEdit2 />}
              colorScheme={isEditing ? "gray" : "blue"}
              onClick={handleEditToggle}
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            {/* Profile Picture & User Info Summary */}
            <VStack align="center" spacing={5}>
              <Box position="relative">
                <Avatar 
                  size="2xl" 
                  name={`${user.first_name} ${user.last_name}`} 
                  src={user.avatar_url}
                  cursor={isEditing ? "pointer" : "default"}
                  onClick={isEditing ? handleAvatarClick : undefined}
                />
                {isEditing && (
                  <Tooltip label="Upload profile picture">
                    <IconButton
                      aria-label="Upload avatar"
                      icon={<FiUpload />}
                      size="sm"
                      colorScheme="blue"
                      rounded="full"
                      position="absolute"
                      bottom="0"
                      right="0"
                      onClick={handleAvatarClick}
                    />
                  </Tooltip>
                )}
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                  accept="image/jpeg, image/png, image/gif"
                />
              </Box>
              <VStack spacing={1} align="center">
                <Heading as="h3" size="md" textAlign="center">
                  {user.first_name} {user.last_name}
                </Heading>
                <Text color="gray.500">{user.job_title || 'No job title'}</Text>
              </VStack>
              <HStack wrap="wrap" justify="center">
                {user.roles?.map((role, index) => (
                  <Badge key={index} colorScheme={getRoleBadgeColor(role)} fontSize="sm" px={2} py={1}>
                    {role}
                  </Badge>
                ))}
              </HStack>
              <Button
                leftIcon={<FiLock />}
                size="sm"
                variant="outline"
                onClick={onOpen}
              >
                Change Password
              </Button>
            </VStack>

            {/* User Details */}
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    name="first_name"
                    value={user.first_name || ''}
                    onChange={handleInputChange}
                    isReadOnly={!isEditing}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    name="last_name"
                    value={user.last_name || ''}
                    onChange={handleInputChange}
                    isReadOnly={!isEditing}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Email Address</FormLabel>
                <Input
                  name="email"
                  value={user.email || ''}
                  onChange={handleInputChange}
                  isReadOnly={!isEditing}
                  type="email"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  name="phone"
                  value={user.phone || ''}
                  onChange={handleInputChange}
                  isReadOnly={!isEditing}
                  type="tel"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Job Title</FormLabel>
                <Input
                  name="job_title"
                  value={user.job_title || ''}
                  onChange={handleInputChange}
                  isReadOnly={!isEditing}
                />
              </FormControl>

              {isEditing && (
                <Button
                  leftIcon={<FiSave />}
                  colorScheme="blue"
                  mt={2}
                  onClick={saveProfile}
                  isLoading={isSaving}
                >
                  Save Changes
                </Button>
              )}
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Additional Settings Tabs */}
      <Card>
        <CardHeader>
          <Heading size="md">Account Settings</Heading>
        </CardHeader>
        <CardBody>
          <Tabs colorScheme="blue" variant="enclosed">
            <TabList>
              <Tab><HStack><FiSettings /><Text ml={2}>Preferences</Text></HStack></Tab>
              <Tab><HStack><FiUser /><Text ml={2}>Account</Text></HStack></Tab>
            </TabList>
            <TabPanels>
              {/* Notification Preferences */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <Text fontWeight="medium" mb={2}>Notification Settings</Text>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Email Updates
                      <Text fontSize="sm" color="gray.500">Receive monthly newsletters and product updates</Text>
                    </FormLabel>
                    <Switch 
                      isChecked={notifications.emailUpdates} 
                      onChange={() => handleNotificationChange('emailUpdates')}
                      colorScheme="blue"
                      isDisabled={!isEditing}
                    />
                  </FormControl>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Content Changes
                      <Text fontSize="sm" color="gray.500">Get notified when content you manage is edited</Text>
                    </FormLabel>
                    <Switch 
                      isChecked={notifications.contentChanges} 
                      onChange={() => handleNotificationChange('contentChanges')}
                      colorScheme="blue" 
                      isDisabled={!isEditing}
                    />
                  </FormControl>
                  
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Security Alerts
                      <Text fontSize="sm" color="gray.500">Receive alerts about login attempts and security updates</Text>
                    </FormLabel>
                    <Switch 
                      isChecked={notifications.securityAlerts} 
                      onChange={() => handleNotificationChange('securityAlerts')}
                      colorScheme="blue"
                      isDisabled={!isEditing}
                    />
                  </FormControl>

                  {isEditing && (
                    <Button
                      leftIcon={<FiSave />}
                      colorScheme="blue"
                      mt={2}
                      onClick={saveProfile}
                      isLoading={isSaving}
                    >
                      Save Preferences
                    </Button>
                  )}
                </VStack>
              </TabPanel>
              
              {/* Account Information */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="medium">Account ID</Text>
                      <Text color="gray.600">{user.user_id}</Text>
                    </Box>
                    
                    <Box>
                      <Text fontWeight="medium">Member Since</Text>
                      <Text color="gray.600">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                  
                  <Box>
                    <Text fontWeight="medium">Current Tenant</Text>
                    <Text color="gray.600">{tenantId}</Text>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Text fontWeight="medium">Access & Permissions</Text>
                    <VStack align="stretch" mt={2}>
                      {user.roles?.map((role, index) => (
                        <HStack key={index} justify="space-between">
                          <Text>{role}</Text>
                          <Badge colorScheme={getRoleBadgeColor(role)}>Active</Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="medium">Last Login</Text>
                    <Text color="gray.600">
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'N/A'}
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* Password Change Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {passwordError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {passwordError}
                </Alert>
              )}
              
              <FormControl isRequired>
                <FormLabel>Current Password</FormLabel>
                <InputGroup>
                  <Input
                    name="currentPassword"
                    type={showPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                  />
                  <InputRightElement>
                    <IconButton
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>New Password</FormLabel>
                <Input
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handlePasswordSubmit}
              isLoading={isSaving}
            >
              Change Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Profile;